/**
 * Hub serviceable-area utilities: pincode, radius, and boundary checks.
 * Used by AddAddress (map + save validation) and CartPage (address selection).
 */

const R = 6371; // Earth radius in km

/** Haversine distance in km between two lat/lng points */
export function distanceKm(lat1, lon1, lat2, lon2) {
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Point-in-polygon (ray casting); path = [{ lat, lng }, ...] */
export function pointInPolygon(lat, lng, path) {
  if (!path || path.length < 3) return false;
  const x = lng;
  const y = lat;
  let inside = false;
  const n = path.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = path[i].lng;
    const yi = path[i].lat;
    const xj = path[j].lng;
    const yj = path[j].lat;
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

/** Serviceable pincodes Set when hub_setup is "pincode" or "radius_and_pincode" */
export function getServiceablePincodesSet(selectedInventory) {
  const set = new Set();
  const hubSetup = selectedInventory?.hub_setup;
  if (
    !selectedInventory ||
    (hubSetup !== "pincode" && hubSetup !== "radius_and_pincode")
  ) {
    return set;
  }
  const pins = selectedInventory.pincodes || [];
  (Array.isArray(pins) ? pins : []).forEach((p) => {
    if (p == null) return;
    if (typeof p === "string" || typeof p === "number") {
      set.add(String(p));
      return;
    }
    if (p.pincode != null) set.add(String(p.pincode));
  });
  return set;
}

/** Radius config for hub_setup "radius" or "radius_and_pincode": [{ lat, lng, radiusKm }, ...] */
export function getRadiusConfig(selectedInventory) {
  const hubSetup = String(selectedInventory?.hub_setup || "").toLowerCase();
  const isRadius = hubSetup === "radius" || hubSetup === "radius_and_pincode";
  if (!selectedInventory || !isRadius) return [];
  const pins = selectedInventory.pincodes || [];
  const list = [];
  (Array.isArray(pins) ? pins : []).forEach((p) => {
    if (p == null || (typeof p !== "object" && typeof p !== "string")) return;
    const lat = parseFloat(p.latitude);
    const lng = parseFloat(p.longitude);
    const radiusKm =
      parseFloat(p.inner_radius) ||
      parseFloat(p.outter_radius) ||
      parseFloat(p.outer_radius);
    const km = !Number.isNaN(radiusKm) && radiusKm > 0 ? radiusKm : 2;
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      list.push({ lat, lng, radiusKm: km });
    }
  });
  return list;
}

/** Boundary polygon paths when hub_setup is "boundary"; each path = [{ lat, lng }, ...] */
export function getBoundaryConfig(selectedInventory) {
  const hubSetup = String(selectedInventory?.hub_setup || "").toLowerCase();
  if (!selectedInventory || hubSetup !== "boundary") return [];
  const pins = selectedInventory.pincodes || [];
  const list = [];
  (Array.isArray(pins) ? pins : []).forEach((p) => {
    if (p == null || typeof p !== "object") return;
    const nLat = parseFloat(p.north_latitude);
    const nLng = parseFloat(p.north_longitude);
    const sLat = parseFloat(p.south_latitude);
    const sLng = parseFloat(p.south_longitude);
    const eLat = parseFloat(p.east_latitude);
    const eLng = parseFloat(p.east_longitude);
    const wLat = parseFloat(p.west_latitude);
    const wLng = parseFloat(p.west_longitude);
    const hasCorners =
      !Number.isNaN(nLat) &&
      !Number.isNaN(nLng) &&
      !Number.isNaN(sLat) &&
      !Number.isNaN(sLng) &&
      !Number.isNaN(eLat) &&
      !Number.isNaN(eLng) &&
      !Number.isNaN(wLat) &&
      !Number.isNaN(wLng);
    if (hasCorners) {
      list.push([
        { lat: nLat, lng: nLng },
        { lat: eLat, lng: eLng },
        { lat: sLat, lng: sLng },
        { lat: wLat, lng: wLng },
      ]);
    }
  });
  return list;
}

/**
 * Whether an address is within the hub's service area (based on hub_setup).
 * addr: { latitude?, longitude?, pin? } (e.g. from API or form)
 * Returns true if no hub config or address is serviceable.
 */
export function isAddressServiceable(addr, selectedInventory, config) {
  if (!selectedInventory || !addr) return true;
  const { serviceablePincodes, radiusConfig, boundaryConfig } = config;
  const hubSetup = String(selectedInventory.hub_setup || "").toLowerCase();
  const pin = String(addr.pin || "").trim();
  const lat = parseFloat(addr.latitude);
  const lng = parseFloat(addr.longitude);
  const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng);

  if (hubSetup === "pincode") {
    if (serviceablePincodes.size === 0) return true;
    return !!pin && serviceablePincodes.has(pin);
  }
  if (hubSetup === "radius") {
    if (radiusConfig.length === 0) return true;
    return (
      hasCoords &&
      radiusConfig.some(({ lat: cLat, lng: cLng, radiusKm }) =>
        distanceKm(lat, lng, cLat, cLng) <= radiusKm
      )
    );
  }
  if (hubSetup === "radius_and_pincode") {
    if (serviceablePincodes.size === 0 && radiusConfig.length === 0) return true;
    const pinOk = !!pin && serviceablePincodes.has(pin);
    const radiusOk =
      hasCoords &&
      radiusConfig.some(({ lat: cLat, lng: cLng, radiusKm }) =>
        distanceKm(lat, lng, cLat, cLng) <= radiusKm
      );
    return pinOk || radiusOk;
  }
  if (hubSetup === "boundary") {
    if (boundaryConfig.length === 0) return true;
    return (
      hasCoords &&
      boundaryConfig.some((path) => pointInPolygon(lat, lng, path))
    );
  }
  return true;
}
